import { create } from "zustand";

/**
 * Which copy the read-only notice shows:
 *  - "role": a write the previewed role genuinely HAS was attempted — claim
 *     "«role» can do this, but preview is read-only". Used by the per-action
 *     guard and by the precise interceptor hit (cap ∈ role's full caps).
 *  - "generic": a 403 we can't attribute to a specific role cap (backend hasn't
 *     stamped `missingCapability` yet) — neutral "not available in read-only
 *     preview; exit to continue as yourself" (always true: the previewer is an
 *     OWNER/ADMIN who regains access on exit).
 */
export type PreviewBlockVariant = "role" | "generic";

/**
 * Tiny UI store for the read-only "preview as role" write-block notice.
 *
 * Kept SEPARATE from {@link usePreviewStore} on purpose: that store owns the
 * crucial ephemeral-token lifecycle (mint / revoke / expiry / cross-tab
 * handoff) and must stay free of transient UI state. This store only tracks
 * whether the read-only explanation dialog is open and which copy to show. The
 * write guard ({@link usePreviewWriteGuard}) and the axios interceptor call
 * `show(...)`; the single {@link PreviewReadOnlyDialog} mounted in the event
 * shell reads it and renders. `show()` is idempotent, so repeated 403s (e.g. a
 * react-query refetch) never stack dialogs.
 */
interface PreviewBlockState {
  open: boolean;
  variant: PreviewBlockVariant;
  /** Open the read-only notice with the given copy (defaults to role-specific). */
  show: (variant?: PreviewBlockVariant) => void;
  /** Close the notice. */
  hide: () => void;
}

export const usePreviewBlockStore = create<PreviewBlockState>((set) => ({
  open: false,
  variant: "role",
  show: (variant: PreviewBlockVariant = "role") => set({ open: true, variant }),
  hide: () => set({ open: false }),
}));
